import React, { FC } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ImageDataLike } from 'gatsby-plugin-image';
import { colors } from '../../styles/tokens';
import { LinkLabel } from '../linkLabel';
import { Image } from '../Image/Image';
import { Spacer } from '../spacer';
import { Typography } from '../typography';

// Interface

export interface UspBlockProps {
  items: UspBlockItemProps[];
}

export interface UspBlockItemProps {
  image?: { image: { localFile: ImageDataLike } };
  stat?: string;
  heading?: string;
  description?: string;
}

// Styles

const StyledUspBlock = styled.div`
  ${({ theme: { space, media, maxMedia } }) => css`
    @media (max-width: ${maxMedia.medium}) {
      > * + * {
        margin-top: ${space.small};
        padding-top: ${space.small};
        border-top: solid 1px ${colors.brandTwo10};
      }
    }

    @media (min-width: ${media.medium}) {
      display: grid;
      grid-template-columns: 1fr 1fr;
      column-gap: ${space.xLarge};

      > * + * {
        position: relative;

        &:before {
          content: '';
          width: 1px;
          height: 100%;
          background-color: ${colors.brandTwo10};
          position: absolute;
          left: -${space.medium};
          top: 0;
        }
      }
    }

    @media (min-width: ${media.medium}) and (max-width: ${maxMedia.large}) {
      position: relative;

      > * {
        padding-top: ${space.medium};
        padding-bottom: ${space.medium};
      }

      &:before {
        content: '';
        height: 1px;
        width: 100%;
        background-color: ${colors.brandTwo10};
        position: absolute;
        left: 0;
        top: 50%;
      }
    }

    @media (min-width: ${media.large}) {
      grid-template-columns: 1fr 1fr 1fr 1fr;
    }
  `};
`;

const StyledTimelineImage = styled.div``;

// JSX

export const UspBlock: FC<UspBlockProps> = ({ items, ...rest }) => {
  if (!items || items.length <= 0) {
    return null;
  }
  return (
    <StyledUspBlock {...rest}>
      {items.map(({ image, heading, stat, description }, index) => (
        <Spacer key={index}>
          <Spacer spacing="small">
            {image && (
              <StyledTimelineImage>
                <Image src={image.image.localFile} alt={heading} />
              </StyledTimelineImage>
            )}
            {stat && (
              <Typography variant="display" component="h3" color="brandTwo100">
                {stat}
              </Typography>
            )}
            {heading && (
              <Typography variant="headingSmall" component="h3">
                {heading}
              </Typography>
            )}
            {description && (
              <Typography component="p" variant="bodyMedium">
                {description}
              </Typography>
            )}
          </Spacer>
        </Spacer>
      ))}
    </StyledUspBlock>
  );
};

export default UspBlock;
